/**
 * The live "what is currently typed into a chat composer" record.
 *
 * Two readers:
 *
 *  - The post-deploy auto-reload (``lib/realtime/use-version-check``) refuses
 *    to reload while any mounted composer holds unsent text, so an auto-update
 *    never discards a half-typed message.
 *  - The native session restore (``lib/session-restore``) copies the text to
 *    device storage when the app goes to the background, because iOS and
 *    Android throw the webview away under memory pressure and the composer
 *    dies with it (KAN-547).
 *
 * Nothing is written to storage from here — this module stays in memory and
 * knows nothing about restoring. Writing is the session-restore module's job,
 * and it only happens at the one moment it can matter.
 */

/** Unsent composer text per session id. Absent = that composer is empty. */
const unsent = new Map<string, string>();

/** True if any mounted composer currently holds unsent text. */
export function composerHasUnsentText(): boolean {
  return unsent.size > 0;
}

/**
 * Record a session's live composer text. Empty (after trimming) drops the
 * entry, so a send — which empties the composer — clears it.
 */
export function setComposerText(sessionId: string, text: string): void {
  if (text.trim().length > 0) unsent.set(sessionId, text);
  else unsent.delete(sessionId);
}

/** Clear a session's entry (composer unmounted, or message sent). */
export function clearComposerText(sessionId: string): void {
  unsent.delete(sessionId);
}

/**
 * The unsent text of the session in view, or `''` when that composer is empty.
 * A read-only peek — it never changes what is recorded.
 */
export function peekComposerText(sessionId: string | null | undefined): string {
  if (!sessionId) return '';
  return unsent.get(sessionId) ?? '';
}
